
const MAX_EXPORT_RECORDS = 10000;

import Vue from "vue";
import store from "@/store";
import { mapState } from "vuex";
import { ROUTE_NAMES } from "@/routes";
import { IRootState } from "@/store/types";
import { DECLINED_TRANSACIONS } from "@/store/declinedTransactions";
import { cloneDeep } from "@/helpers/object";
import { date, dateGreaterThan, dateLessThan } from "@/validators";
import { DECLINED_TRANSACTIONS_ROUTE_NAMES } from "@/declined-transactions-routes";
import { maxValue } from "vuelidate/lib/validators";

export default Vue.extend({
  data() {
    return {
      rawStartDate: undefined,
      rawEndDate: undefined,
      isLoading: false,
      isFirstLoading:
        !store.state.declinedTransactions.hasSearchedForDeclinedTransactions,
      areFiltersDirty: false,
      hasFailedLoad: false,
      filters: cloneDeep(
        store.state.declinedTransactions.dataTables.declinedTransactionsList
          .declinedTransactionFilters
      ),
      headers: [
        { sortable: false, text: "Card Number", value: "cardNumber" },
        { sortable: false, text: "Location Code", value: "locationCode" },
        { sortable: false, text: "Location Name", value: "locationName" },
        {
          sortable: false,
          text: "Parent / Child Terminal ID",
          value: "parentTerminalId",
        },
        { sortable: false, text: "Decline Code", value: "declineCode" },
        { sortable: false, text: "Decline Reason", value: "declineReason" },
        {
          sortable: false,
          text: "Transaction Date",
          value: "transactionDateTime",
        },
        {
          sortable: false,
          text: "Transaction Number",
          value: "voucherTransationNumber",
        },
        {
          sortable: false,
          text: "Total Transaction Value",
          value: "totalTransactionValue",
        },
      ],
    };
  },
  computed: {
    pagination: {
      get() {
        return store.state.declinedTransactions.dataTables
          .declinedTransactionsList.pagination;
      },
      set(value: string) {
        this.$store.commit(
          DECLINED_TRANSACIONS.NS(
            DECLINED_TRANSACIONS.MUTATIONS.SET_DECLINED_TRANSACTIONS_PAGINATION
          ),
          value
        );
      },
    },
    exceedsMaxRecords: {
      get() {
        return this.totalDeclinedTransacitons > MAX_EXPORT_RECORDS;
      }
    },
    decline_codes: {
      get() {
        return store.state.declinedTransactions.declineCodes.map(
          (code) => code.declineCode
        );
      },
    },
    ...mapState({
      hasSearched: (state: IRootState) =>
        state.declinedTransactions.hasSearchedForDeclinedTransactions,
      declinedTransactionsList: (state: IRootState) =>
        state.declinedTransactions.declinedTransactionsList,
      totalDeclinedTransacitons: (state: IRootState) =>
        state.declinedTransactions.totalDeclinedTranasctionCount,
      declineCodes: (state: IRootState) =>
        state.declinedTransactions.declineCodes,
    }),
    route: () => DECLINED_TRANSACTIONS_ROUTE_NAMES.DECLINED_TRANSACTION_DETAIL,
  },
  methods: {
    /**
     * Run when the table updates it's pagination
     */
    async onPagination() {
      await this.updateResults();
    },
    /**
     * Run when filters are updated, used to enforce that the export must
     * happen after a search
     */
    async markFiltersDirty() {
      this.areFiltersDirty = true;
    },
    /*
      Run when the search button is clicked
    */
    async onSearch() {
      // When the search button is pressed, we want to reset the pagination
      this.pagination.page = 1;

      this.$store.commit(
        DECLINED_TRANSACIONS.NS(
          DECLINED_TRANSACIONS.MUTATIONS.SET_DECLINED_TRANSACTIONS_PAGINATION
        ),
        cloneDeep(this.pagination)
      );

      // Total transaction value can be an empty string sometimes because of veutify weirdness
      this.filters.totalTransactionValue =
        parseFloat(this.filters.totalTransactionValue) || undefined;

      this.$store.commit(
        DECLINED_TRANSACIONS.NS(
          DECLINED_TRANSACIONS.MUTATIONS.SET_DECLINED_TRANASCTIONS_FILTERS
        ),
        cloneDeep(this.filters)
      );

      this.areFiltersDirty = false;

      // Fetch updated results.
      await this.updateResults();
    },
    /**
     * Run whenver the table needs to have it's data refreshed
     */
    async updateResults() {
      this.isLoading = true;
      if (!this.hasSearched) this.hasSearched = true;

      await this.$store.dispatch(
        DECLINED_TRANSACIONS.NS(
          DECLINED_TRANSACIONS.ACTIONS.LOAD_DECLINED_TRANSACTIONS_LIST
        ),
        {
          pagination: this.pagination,
        }
      );

      this.isLoading = false;
    },
    /**
     * Runs on first load, used to give the nice display and provide time
     * for the function to warm up as needed.
     *
     * Also needed as the decline codes are used in the filter select.
     */
    async firstLoad(attempts: number) {
      // Give us thirty seconds to load before giving up
      if (attempts > 30) {
        this.hasFailedLoad = true;
        return;
      }

      // The first load can be expected to fail a few times if the function needs to warm up.
      try {
        await this.$store.dispatch(
          DECLINED_TRANSACIONS.NS(
            DECLINED_TRANSACIONS.ACTIONS.LOAD_DECLINE_CODES
          )
        );

        this.isFirstLoading = false;
      } catch {
        setTimeout(() => this.firstLoad(attempts + 1), 1000);
      }
    },
    goToDetails(id: any) {
      this.$router.push({
        name: DECLINED_TRANSACTIONS_ROUTE_NAMES.DECLINED_TRANSACTION_DETAIL,
        params: { id },
      });
    },
  },
  async mounted() {
    if (!this.hasSearched) this.firstLoad(0);
  },
  validations() {
    return {
      rawStartDate: {
        date,
      },
      rawEndDate: {
        date,
      },
      filters: {
        startDateTime: {
          lessThan: dateLessThan("endDateTime"),
          inPast: maxValue(new Date()),
          date,
        },
        endDateTime: {
          greaterThan: dateGreaterThan("startDateTime"),
          date,
        },
      },
    };
  },
});
